import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RecaptchaVerifier, signInWithPhoneNumber, type ConfirmationResult } from "firebase/auth";

import { auth } from "../../firebaseConfig";
import { useAuth } from "../../contexts/AuthContext";
import logo from '../../assets/image/logo.jpg';


export default function Login() {
  const navigate = useNavigate();
  const { role, user } = useAuth(); // Added useAuth hook call
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);

  const [debugError, setDebugError] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);

  // Format phone number as XXX-XXXX-XXXX
  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    // Apply formatting: XXX-XXXX-XXXX
    if (digits.length < 4) {
      return digits;
    } else if (digits.length < 8) {
      // 4-7 digits: XXX-XXXX
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
      // 8-11 digits: XXX-XXXX-XXXX
      return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
    }
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  const handleCodeChange = (index: number, value: string) => {
    // Should be digits only
    if (!/^\d*$/.test(value)) return;

    if (value.length > 1) {
      // Handle paste or autofill (e.g., from iOS keyboard "From Messages")
      const newCode = value.split('').slice(0, 6);
      const updatedCode = [...verificationCode.split('')];

      // Ensure array is long enough (fill with empty strings if needed)
      while (updatedCode.length < 6) updatedCode.push('');

      for (let i = 0; i < newCode.length; i++) {
        if (index + i < 6) {
          updatedCode[index + i] = newCode[i];
        }
      }

      const newCodeStr = updatedCode.join('').slice(0, 6);
      setVerificationCode(newCodeStr);

      // Focus the next empty input or the last one
      const nextIndex = Math.min(index + newCode.length, 5);
      const nextInput = document.getElementById(`code-${nextIndex}`);
      nextInput?.focus();
      return;
    }

    // Normal single character input
    const newCode = verificationCode.split('');
    while (newCode.length < 6) newCode.push('');

    newCode[index] = value;
    const newCodeStr = newCode.join('').slice(0, 6);
    setVerificationCode(newCodeStr);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6).replace(/\D/g, '');
    if (pastedData) {
      setVerificationCode(pastedData);
      // Focus the last filled input or the next empty one
      const nextIndex = Math.min(pastedData.length, 5);
      document.getElementById(`code-${nextIndex}`)?.focus();
    }
  };


  useEffect(() => {
    if (user && role) {
      if (role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/home');
      }
    }
  }, [user, role, navigate]);

  useEffect(() => {
    // Set language to Japanese
    auth.languageCode = 'ja';

    // Clean up any existing verifier to avoid "client element has been removed" error
    // when React re-renders the DOM node but the verifier still points to the old one.
    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.clear();
      } catch (e) {
        console.error("Error clearing existing verifier:", e);
      }
      window.recaptchaVerifier = null;
    }

    try {
      const container = document.getElementById('recaptcha-container');
      if (!container) {
        console.error("recaptcha-container not found in DOM");
        setDebugError("Internal Error: reCAPTCHA container missing.");
        return;
      }

      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        'size': 'normal',
        'callback': () => {
          // reCAPTCHA solved, allow signInWithPhoneNumber.
          console.log("reCAPTCHA solved");
          setDebugError(null);
        },
        'expired-callback': () => {
          // Response expired. Ask user to solve reCAPTCHA again.
          console.log("reCAPTCHA expired");
          setDebugError("reCAPTCHA expired. Please try again.");
        }
      });


      // Render immediately to ensure widget is ready
      window.recaptchaVerifier.render().then((widgetId: number) => {
        console.log("reCAPTCHA rendered with ID:", widgetId);
      }).catch((err: Error) => {
        console.error("Error rendering reCAPTCHA:", err);
        setDebugError(`Error rendering reCAPTCHA: ${err.message}`);
      });

    } catch (err: any) {
      console.error("Error initializing reCAPTCHA:", err);
      setDebugError(`Error initializing reCAPTCHA: ${err.message}`);
    }

    return () => {
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {
          console.error("Error clearing verifier on unmount:", e);
        }
        window.recaptchaVerifier = null;
      }
    };
  }, []);

  const handleSendCode = async () => {
    if (phoneNumber.length >= 10) {
      setDebugError(null);
      setError(null);
      console.log("Starting handleSendCode...");
      try {
        const appVerifier = window.recaptchaVerifier;
        if (!appVerifier) {
          console.error("RecaptchaVerifier not initialized");
          throw new Error("RecaptchaVerifier not initialized");
        }
        console.log("RecaptchaVerifier found:", appVerifier);

        // Format phone number to E.164 format
        // 1. Remove hyphens
        let formattedPhoneNumber = phoneNumber.replace(/-/g, '');

        // 2. If starts with 0, replace with +81 (Japan country code)
        if (formattedPhoneNumber.startsWith('0')) {
          formattedPhoneNumber = '+81' + formattedPhoneNumber.slice(1);
        }
        console.log("Formatted phone number:", formattedPhoneNumber);

        console.log("Calling signInWithPhoneNumber...");
        console.log("Auth object state:", auth); // Log auth object
        const result = await signInWithPhoneNumber(auth, formattedPhoneNumber, appVerifier);
        console.log("signInWithPhoneNumber success:", result);

        setConfirmationResult(result);
        setIsCodeSent(true);

        // Hide reCAPTCHA after successful SMS send
        const recaptchaContainer = document.getElementById('recaptcha-container');
        if (recaptchaContainer) {
          recaptchaContainer.style.display = 'none';
        }

      } catch (error: any) {
        console.error("Error sending SMS code", error);

        // Detailed error logging
        console.error("Error Code:", error.code);
        console.error("Error Message:", error.message);
        console.error("Error Stack:", error.stack);
        console.error("Error CustomData:", error.customData);
        console.error("Raw Error Object:", JSON.stringify(error, Object.getOwnPropertyNames(error)));

        setDebugError(`Error sending SMS code: ${error.message} (Code: ${error.code})\nDetails: ${JSON.stringify(error, Object.getOwnPropertyNames(error))}`);
        setError("SMSの送信に失敗しました。電話番号を確認してください。");
        // Do NOT clear the verifier here. Just let the user try again.
        // If we clear it, the widget disappears and we'd need to re-init.
        // window.recaptchaVerifier.clear(); 
      }
    }
  };

  const handleLogin = async () => {
    if (verificationCode.length === 6 && confirmationResult) {
      try {
        setError(null);
        await confirmationResult.confirm(verificationCode);
        // Navigation will be handled by the useEffect above once AuthContext updates

      } catch (error: any) {
        console.error("Error verifying code", error);
        setDebugError(`Error verifying code: ${error.message}`);
        setError("認証コードが正しくありません。");
        alert("認証コードが正しくありません。");
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-sm p-8">
        {/* Logo Area */}
        <div className="text-center mb-8">
          <img
            src={logo}
            alt="ラーキャリ"
            className="h-16 mx-auto object-contain mb-6"
          />
          <h1 className="text-xl font-bold text-gray-800">
            {isCodeSent ? '認証コードを入力' : '電話番号を入力'}
          </h1>
          <p className="text-sm text-gray-500 mt-2">
            {isCodeSent
              ? 'SMSに届いた6桁の数字を入力してください'
              : '試験を受けるにはログインが必要です'}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 rounded-xl text-sm flex items-start gap-3">
            <i className="ri-error-warning-line text-lg shrink-0 mt-0.5"></i>
            <span>{error}</span>
          </div>
        )}

        {!isCodeSent ? (
          <div className="space-y-6">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <i className="ri-smartphone-line text-gray-400 text-xl"></i>
              </div>
              <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9-]*"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                placeholder="090-1234-5678"
                maxLength={15}
                className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all bg-gray-50 text-gray-800 placeholder-gray-400 text-center text-xl tracking-widest font-medium"
              />
            </div>

            <div id="recaptcha-container" className="flex justify-center my-4 transform scale-75 origin-center overflow-visible"></div>

            <button
              onClick={handleSendCode}
              disabled={!phoneNumber || phoneNumber.length < 10}
              className="w-full bg-[#F4B183] text-white font-bold py-4 rounded-xl hover:bg-[#E5A072] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              認証コードを送信
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex justify-center gap-2">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  id={`code-${index}`}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6} // Allow autofill to put typically 6 chars in the first field temporarily
                  value={verificationCode[index] || ''}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="w-12 h-14 border border-gray-200 rounded-xl text-center text-2xl font-bold focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all bg-gray-50 text-gray-800"
                />
              ))}
            </div>

            <button
              onClick={handleLogin}
              disabled={verificationCode.length !== 6}
              className="w-full bg-[#F4B183] text-white font-bold py-4 rounded-xl hover:bg-[#E5A072] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              ログイン
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
